import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Upload, Trophy, Users } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

export default function GuessingGame() {
  const { toast } = useToast();

  const guesses = [
    { name: "Sarah Smith", guess: "2.5 kg", time: "2 hours ago" },
    { name: "John Doe", guess: "2.8 kg", time: "3 hours ago" },
    { name: "Emma Wilson", guess: "2.3 kg", time: "5 hours ago" },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Guessing Game</h1>
        <p className="text-muted-foreground mt-2">
          Create interactive guessing games for your events
        </p>
        <Badge className="mt-2 bg-primary">Standard Plan Feature</Badge>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Create Game */}
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle>Create New Game</CardTitle>
            <CardDescription>Set up a guessing game with prizes</CardDescription>
          </CardHeader>
          <CardContent>
            <Dialog>
              <DialogTrigger asChild>
                <Button className="w-full" size="lg">Create Game</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Create Guessing Game</DialogTitle>
                  <DialogDescription>Set up your guessing game details</DialogDescription>
                </DialogHeader>
                <form className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="game-type">Game Type</Label>
                    <Select>
                      <SelectTrigger id="game-type">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weight">Guess the Weight</SelectItem>
                        <SelectItem value="number">Guess the Number</SelectItem>
                        <SelectItem value="quantity">Guess the Quantity</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="game-name">Game Name</Label>
                    <Input id="game-name" placeholder="e.g., Guess the Weight of the Cake" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="game-image">Upload Image</Label>
                    <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-colors cursor-pointer">
                      <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Click to upload or drag and drop</p>
                      <p className="text-xs text-muted-foreground mt-1">PNG, JPG up to 10MB</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="game-price">Entry Price</Label>
                      <Input id="game-price" placeholder="£1.00" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="game-answer">Correct Answer</Label>
                      <Input id="game-answer" placeholder="2.5 kg" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Winner Selection</Label>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2">
                        <input type="radio" name="selection" defaultChecked />
                        <span className="text-sm">Automatic (closest guess)</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input type="radio" name="selection" />
                        <span className="text-sm">Manual selection</span>
                      </label>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline">Cancel</Button>
                    <Button type="submit">Create Game</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            <div className="mt-4 p-4 bg-muted/30 rounded-lg">
              <h4 className="font-semibold text-sm mb-2">Game Features:</h4>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• Custom image upload</li>
                <li>• No duplicate guesses allowed</li>
                <li>• Automatic winner selection</li>
                <li>• Results emailed to participants</li>
                <li>• Winners displayed on page</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Active Game */}
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle>Active Game</CardTitle>
            <CardDescription>Guess the Weight of the Cake</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
              <div className="text-center">
                <p className="text-6xl mb-2">🎂</p>
                <p className="text-sm text-muted-foreground">Cake Image</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground">Total Entries</p>
                <p className="text-2xl font-bold text-primary">45</p>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground">Entry Price</p>
                <p className="text-2xl font-bold">£1.00</p>
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <h4 className="font-semibold mb-3">Recent Guesses</h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {guesses.map((entry, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                    <div>
                      <p className="font-medium text-sm">{entry.name}</p>
                      <p className="text-xs text-muted-foreground">{entry.time}</p>
                    </div>
                    <span className="font-bold text-primary">{entry.guess}</span>
                  </div>
                ))}
              </div>
            </div>

            <Button className="w-full" size="lg">
              <Trophy className="h-4 w-4 mr-2" />
              Select Winner
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Past Games */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle>Completed Games</CardTitle>
          <CardDescription>View past game results</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="p-4 border border-border rounded-lg">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-semibold">Guess the Jelly Beans</h4>
                  <p className="text-sm text-muted-foreground">Completed: May 5, 2025</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Users className="h-4 w-4 text-primary" />
                    <span className="text-sm">67 entries</span>
                  </div>
                  <p className="text-sm mt-2 font-medium">Winner: Emma Wilson (guessed 487, actual: 485)</p>
                </div>
                <Badge variant="secondary">Completed</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
